import { isTauri } from '@/lib/utils/tauri'

// Presentation only. Heartbeats, recording, uploads and notifications must not
// depend on this store. An unfocused but visible window still needs to paint.
let visible =
  typeof document === 'undefined' || document.visibilityState !== 'hidden'
const listeners = new Set<() => void>()
let cleanup: (() => void) | undefined

export const getPresentationVisibility = () => visible

export function subscribePresentationVisibility(listener: () => void) {
  listeners.add(listener)
  if (listeners.size === 1) {
    let disposed = false
    let nativeVisible = true
    let revision = 0
    const unlisteners: Array<() => void> = []
    const publish = () => {
      const next = nativeVisible && document.visibilityState !== 'hidden'
      if (visible === next) return
      visible = next
      listeners.forEach(notify => notify())
    }
    document.addEventListener('visibilitychange', publish)
    publish()
    if (isTauri()) {
      void import('@tauri-apps/api/window')
        .then(async ({ getCurrentWindow }) => {
          const window = getCurrentWindow()
          const refresh = async () => {
            const request = ++revision
            const [shown, minimized] = await Promise.all([
              window.isVisible(),
              window.isMinimized(),
            ])
            if (disposed || request !== revision) return
            nativeVisible = shown && !minimized
            publish()
          }
          const track = (unlisten: () => void) => {
            if (disposed) unlisten()
            else unlisteners.push(unlisten)
          }
          track(
            await window.listen<boolean>(
              'desktop:presentation-visibility',
              event => {
                if (disposed) return
                ++revision
                nativeVisible = event.payload
                publish()
              },
            ),
          )
          if (disposed) return
          track(
            await window.onFocusChanged(() => {
              void refresh().catch(() => {})
            }),
          )
          if (disposed) return
          track(
            await window.onResized(() => {
              void refresh().catch(() => {})
            }),
          )
          if (!disposed) await refresh()
        })
        .catch(() => {
          // Browser visibility remains a conservative fallback if native setup fails.
        })
    }
    cleanup = () => {
      disposed = true
      ++revision
      document.removeEventListener('visibilitychange', publish)
      unlisteners.forEach(unlisten => unlisten())
    }
  }
  return () => {
    listeners.delete(listener)
    if (!listeners.size) {
      cleanup?.()
      cleanup = undefined
    }
  }
}
